/**
 * Industry-style defaults for solar plant equipment counts (tunable constants).
 * Non-technical copy lives in the wizard; formulas stay here.
 */

import { ObjectId } from 'bson';
import { mapToBackendAssetType } from '../../utils/assetTypeMapper';
import type { ResolvedSolarCatalog } from './solarSetupCatalog';
import type { VisualEquipment } from './types';
import { TREE_PERF } from './constants';

/** Assumed DC nameplate per module (kW) for rough DC capacity sizing */
const DEFAULT_KW_PER_MODULE = 0.55;
const MIN_MODULES_PER_STRING = 8;
const MAX_MODULES_PER_STRING = 28;
const DEFAULT_STRINGS_PER_INVERTER = 8;
const MAX_STRINGS_PER_INVERTER = 16;
const SUGGEST_COMBINER_IF_STRINGS_GT = 8;

export type CapacityInput = {
  value: number;
  unit: 'MW' | 'KW';
};

export type SolarPlanCounts = {
  inverters: number;
  stringsPerInverter: number;
  modulesPerString: number;
  combinerCount: number;
  transformerCount: number;
  includeAcSide: boolean;
  includeDcCable: boolean;
};

export type WizardAnswers = {
  capacity: CapacityInput;
  mountType: 'ground' | 'rooftop' | null;
  designStyle: 'standard' | 'customize' | null;
  useAutoCounts: boolean | null;
  /** Manual / edited counts */
  counts: Partial<SolarPlanCounts>;
};

export function capacityToKw(input: CapacityInput): number {
  if (input.unit === 'MW') return input.value * 1000;
  return input.value;
}

/**
 * Suggest inverter/string/module/combiner/transformer counts from DC capacity.
 */
export function suggestEquipmentCounts(capacity: CapacityInput): SolarPlanCounts {
  const capacityKw = capacityToKw(capacity);
  const targetModules = Math.max(4, Math.round(capacityKw / DEFAULT_KW_PER_MODULE));

  const stringsPerInverter = Math.min(
    MAX_STRINGS_PER_INVERTER,
    DEFAULT_STRINGS_PER_INVERTER
  );
  const inverters = Math.max(
    1,
    Math.ceil(targetModules / (stringsPerInverter * MIN_MODULES_PER_STRING))
  );

  const totalStrings = inverters * stringsPerInverter;
  let modulesPerString = Math.ceil(targetModules / totalStrings);
  modulesPerString = Math.min(MAX_MODULES_PER_STRING, Math.max(MIN_MODULES_PER_STRING, modulesPerString));

  const combinerCount =
    totalStrings > SUGGEST_COMBINER_IF_STRINGS_GT
      ? Math.max(1, Math.ceil(stringsPerInverter / SUGGEST_COMBINER_IF_STRINGS_GT))
      : 0;

  const transformerCount = Math.min(inverters, Math.max(1, Math.ceil(inverters / 4)));

  return {
    inverters,
    stringsPerInverter,
    modulesPerString,
    combinerCount,
    transformerCount,
    includeAcSide: true,
    includeDcCable: false
  };
}

export type ValidationResult = {
  ok: boolean;
  messages: string[];
  fixed?: SolarPlanCounts;
};

export function validateSolarPlanCounts(c: SolarPlanCounts): ValidationResult {
  const messages: string[] = [];
  if (c.inverters < 1) messages.push('At least one inverter is required.');
  if (c.stringsPerInverter < 1) messages.push('Each inverter needs at least one string.');
  if (c.modulesPerString < 1) messages.push('Each string needs at least one module.');
  if (c.combinerCount > 0 && c.combinerCount > c.stringsPerInverter) {
    messages.push('String combiner boxes per inverter cannot exceed strings per inverter.');
  }
  if (c.includeAcSide && c.transformerCount < 1) {
    messages.push('When including AC equipment, at least one transformer is typical.');
  }

  if (messages.length === 0) return { ok: true, messages: [] };

  const fixed: SolarPlanCounts = { ...c };
  fixed.inverters = Math.max(1, fixed.inverters);
  fixed.stringsPerInverter = Math.max(1, fixed.stringsPerInverter);
  fixed.modulesPerString = Math.max(1, Math.min(MAX_MODULES_PER_STRING, fixed.modulesPerString));
  if (fixed.combinerCount > fixed.stringsPerInverter) {
    fixed.combinerCount = fixed.stringsPerInverter;
  }
  if (fixed.combinerCount < 0) fixed.combinerCount = 0;
  if (fixed.includeAcSide && fixed.transformerCount < 1) fixed.transformerCount = 1;

  return { ok: false, messages, fixed };
}

/**
 * Embedded DC structure — no separate equipment for strings/modules.
 * On **inverter**: totals + combiner count; per-string modules live on each **SCB** when `combinerBoxCount > 0`.
 * On **combiner (SCB)**: `stringsPerInverter` = strings on this box; `combinerBoxCount` should be 0.
 */
/** Persisted equipment that holds embedded strings/modules layout (linked under inverter or SCB). */
export const SOLAR_DC_LAYOUT_EQUIPMENT_TYPE = 'Solar DC Layout';

export type SolarDcLayout = {
  stringsPerInverter: number;
  modulesPerString: number;
  combinerBoxCount: number;
  combinerStringDistribution?: number[];
  /** Optional nested dimensions (e.g. 28 SCB each with 28 strings) — must align with totals when set */
  combinersPerInverter?: number;
  stringsPerCombiner?: number;
  /** Per-string module counts when strings differ (e.g. 27 strings × 30 + 1 × 29); length should match stringsPerInverter */
  stringModuleCounts?: number[];
};

export type InverterSolarDcLayoutOptions = {
  combinerBoxCount?: number;
  combinerStringDistribution?: number[];
  combinersPerInverter?: number;
  stringsPerCombiner?: number;
};

/**
 * Build `solarDcLayout` for an inverter from per-string module counts (bulk / manual grouping).
 * `modulesPerString` is set to max(counts) for summaries and even virtual rows fallback.
 */
/** Inverter-only summary when strings/modules are stored on child SCB rows (no `stringModuleCounts` on inverter). */
export function inverterSolarDcLayoutAggregatedForCombiners(params: {
  totalStrings: number;
  modulesPerStringUniform: number;
  combinerBoxCount: number;
  combinerStringDistribution: number[];
  evenStringsPerCombiner?: number;
}): SolarDcLayout {
  return {
    stringsPerInverter: params.totalStrings,
    modulesPerString: Math.max(1, params.modulesPerStringUniform),
    combinerBoxCount: params.combinerBoxCount,
    combinerStringDistribution: [...params.combinerStringDistribution],
    combinersPerInverter: params.combinerBoxCount,
    ...(params.evenStringsPerCombiner != null
      ? { stringsPerCombiner: params.evenStringsPerCombiner }
      : {})
  };
}

export function inverterSolarDcLayoutFromStringModules(
  stringModuleCounts: number[],
  options?: InverterSolarDcLayoutOptions
): SolarDcLayout {
  const stringsPerInverter = stringModuleCounts.length;
  if (stringsPerInverter < 1) {
    throw new Error('At least one string module count is required.');
  }
  const modulesPerString = Math.max(1, ...stringModuleCounts);
  return {
    stringsPerInverter,
    modulesPerString,
    combinerBoxCount: options?.combinerBoxCount ?? 0,
    stringModuleCounts: [...stringModuleCounts],
    ...(options?.combinerStringDistribution?.length
      ? { combinerStringDistribution: options.combinerStringDistribution }
      : {}),
    ...(options?.combinersPerInverter != null ? { combinersPerInverter: options.combinersPerInverter } : {}),
    ...(options?.stringsPerCombiner != null ? { stringsPerCombiner: options.stringsPerCombiner } : {})
  };
}

/**
 * Max virtual nodes (strings + modules) to materialize in the UI.
 * Above this, a single summary node is shown to avoid tens of thousands of React objects.
 */
export const SOLAR_VIRTUAL_EXPAND_MAX_NODES = 1500;

function modulesForStringIndex(layout: SolarDcLayout, stringIndex1: number): number {
  const arr = layout.stringModuleCounts;
  if (
    arr &&
    arr.length === layout.stringsPerInverter &&
    stringIndex1 >= 1 &&
    stringIndex1 <= arr.length
  ) {
    const n = arr[stringIndex1 - 1];
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0) {
      return Math.max(0, Math.floor(n));
    }
  }
  return layout.modulesPerString;
}

/**
 * Aggregated counts for the inline "full DC layout" panel design
 * (strings + modules rendered inside one equipment card — no virtual tree children).
 */
export function computeSolarDcLayoutTotals(layout: SolarDcLayout): {
  totalStrings: number;
  totalModules: number;
  maxModulesPerString: number;
  perStringModules: number[];
} {
  const totalStrings = Math.max(0, layout.stringsPerInverter | 0);
  const perStringModules: number[] = [];
  let totalModules = 0;
  let maxModulesPerString = 0;
  for (let s = 1; s <= totalStrings; s++) {
    const n = modulesForStringIndex(layout, s);
    perStringModules.push(n);
    totalModules += n;
    if (n > maxModulesPerString) maxModulesPerString = n;
  }
  if (maxModulesPerString === 0) {
    maxModulesPerString = Math.max(0, layout.modulesPerString | 0);
  }
  return { totalStrings, totalModules, maxModulesPerString, perStringModules };
}

/** How many VisualEquipment rows full expansion would create: S string rows + per-string module rows */
export function countSolarVirtualNodesIfExpanded(layout: SolarDcLayout): number {
  let total = 0;
  for (let s = 1; s <= layout.stringsPerInverter; s++) {
    total += 1 + modulesForStringIndex(layout, s);
  }
  return total;
}

export function isSolarDcLayout(v: unknown): v is SolarDcLayout {
  if (v == null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.stringsPerInverter === 'number' &&
    typeof o.modulesPerString === 'number' &&
    typeof o.combinerBoxCount === 'number'
  );
}

/** True when layout already includes per-string module counts (GET /equipment/:id or legacy tree payload). */
export function hasFullStringModuleDetail(layout: unknown): boolean {
  if (!isSolarDcLayout(layout)) return false;
  if (layout.stringsPerInverter < 1) return false;
  const arr = layout.stringModuleCounts;
  return Array.isArray(arr) && arr.length === layout.stringsPerInverter;
}

function summarizeStringModuleCounts(counts: number[]): string {
  const map = new Map<number, number>();
  for (const c of counts) {
    const n = Math.max(0, Math.floor(c));
    if (n < 1) continue;
    map.set(n, (map.get(n) ?? 0) + 1);
  }
  const parts = [...map.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([m, n]) => `${n}×${m}`);
  return parts.length ? parts.join(', ') : '';
}

function formatDcStructureSummaryLabel(inverterName: string, layout: SolarDcLayout): string {
  const scb = layout.combinerBoxCount;
  const str = layout.stringsPerInverter;
  const mod = layout.modulesPerString;
  const uneven =
    layout.stringModuleCounts &&
    layout.stringModuleCounts.length === layout.stringsPerInverter &&
    layout.stringModuleCounts.some((x) => x !== mod);
  const totalMods = uneven
    ? layout.stringModuleCounts!.reduce((a, b) => a + b, 0)
    : str * mod;
  const comb = layout.combinersPerInverter;
  const spc = layout.stringsPerCombiner;
  if (
    comb != null &&
    comb > 0 &&
    spc != null &&
    spc > 0 &&
    scb > 0
  ) {
    return `DC structure · ${comb} SCB × ${spc} strings × ${mod} modules (${totalMods} modules total)`;
  }
  const parts: string[] = [];
  if (scb > 0) parts.push(`${scb} SCB`);
  parts.push(`${str} strings`);
  if (uneven && layout.stringModuleCounts) {
    const sm = summarizeStringModuleCounts(layout.stringModuleCounts);
    parts.push(sm ? `${totalMods} modules (${sm})` : `${totalMods} modules`);
  } else {
    parts.push(`${totalMods} modules (M1…M${mod})`);
  }
  return `DC structure · ${parts.join(' · ')}`;
}

function buildDcStructureSummaryNode(
  inverterEquipmentId: string,
  inverterName: string,
  layout: SolarDcLayout
): VisualEquipment {
  return {
    id: `${inverterEquipmentId}::dc-struct`,
    parentEquipmentId: inverterEquipmentId,
    childEquipmentIds: [],
    name: formatDcStructureSummaryLabel(inverterName, layout),
    assetType: 'DC structure (summary)',
    assetCategory: 'other',
    isVirtual: true,
    isDcStructureSummary: true,
    projectAssetName: 'DC structure (summary)'
  };
}

export type BulkPlanItem = {
  _id: string;
  equipmentName: string;
  parentId: string | null;
  assetId: string;
  projectId: string;
  assetType: string;
  hierarchyLevel: string;
  connections?: Array<{ connectedTo: string; type: 'DC' | 'AC' | 'HT' }>;
  zone?: string;
  solarDcLayout?: SolarDcLayout;
};

function displayTypeForRole(
  role: 'pvModule' | 'string' | 'inverter' | 'dcCable' | 'acCable' | 'ltPanel' | 'transformer',
  cat: ResolvedSolarCatalog
): string {
  const d = cat[role]?.displayName;
  if (role === 'pvModule') return d || 'Solar Module';
  return d || role;
}

/**
 * Build flat bulk-create items: Inverter + optional SCB rows + separate **Solar DC Layout** equipment
 * (PV module project asset) holding `solarDcLayout` under inverter or each SCB + DC/AC chain.
 * Strings/modules stay virtual in the UI under that layout row.
 */
export function buildSolarWizardBulkPlan(
  projectId: string,
  counts: SolarPlanCounts,
  catalog: ResolvedSolarCatalog
): { items: BulkPlanItem[]; summary: string } {
  const invR = catalog.inverter;
  if (!invR) {
    throw new Error('Missing required catalog role: inverter.');
  }
  const pvR = catalog.pvModule;
  if (!pvR) {
    throw new Error(
      'Missing required catalog role: pvModule (add a PV Module / solar panel master asset to the project).'
    );
  }

  const dcR = catalog.dcCable;
  const acR = catalog.acCable;
  const ltR = catalog.ltPanel;
  const trR = catalog.transformer;

  const items: BulkPlanItem[] = [];
  const totalStrings = counts.inverters * counts.stringsPerInverter;
  const useCombiner = counts.combinerCount > 0;
  const combR = catalog.combiner;
  if (useCombiner && !combR) {
    throw new Error('Missing required catalog role: combiner (add a String Combiner / SCB master asset).');
  }
  const combinerStringDistribution =
    useCombiner && counts.combinerCount > 0
      ? distributeStringsAcrossCombiners(counts.stringsPerInverter, counts.combinerCount)
      : undefined;
  const evenStringsPerCombiner =
    useCombiner &&
    counts.combinerCount > 0 &&
    counts.stringsPerInverter % counts.combinerCount === 0
      ? counts.stringsPerInverter / counts.combinerCount
      : undefined;

  for (let i = 1; i <= counts.inverters; i++) {
    const invId = new ObjectId().toString();
    const invName = `INV-${i}`;
    const invItem: BulkPlanItem = {
      _id: invId,
      equipmentName: invName,
      parentId: null,
      assetId: invR.projectAssetId,
      projectId,
      assetType: 'Inverter',
      hierarchyLevel: 'Inverter',
      connections: [],
      zone: 'Z01'
    };
    if (useCombiner) {
      invItem.solarDcLayout = {
        stringsPerInverter: counts.stringsPerInverter,
        modulesPerString: counts.modulesPerString,
        combinerBoxCount: counts.combinerCount,
        ...(combinerStringDistribution ? { combinerStringDistribution } : {}),
        ...(counts.combinerCount > 0 ? { combinersPerInverter: counts.combinerCount } : {}),
        ...(evenStringsPerCombiner != null ? { stringsPerCombiner: evenStringsPerCombiner } : {})
      };
    }
    items.push(invItem);

    if (!useCombiner) {
      const dcLayId = new ObjectId().toString();
      items.push({
        _id: dcLayId,
        equipmentName: `${invName} · DC`,
        parentId: invId,
        assetId: pvR.projectAssetId,
        projectId,
        assetType: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
        hierarchyLevel: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
        connections: [],
        zone: 'Z01',
        solarDcLayout: {
          stringsPerInverter: counts.stringsPerInverter,
          modulesPerString: counts.modulesPerString,
          combinerBoxCount: 0
        }
      });
    }

    if (useCombiner && combR && combinerStringDistribution && counts.combinerCount > 0) {
      for (let j = 0; j < counts.combinerCount; j++) {
        const nStr = combinerStringDistribution[j] ?? 0;
        const scbId = new ObjectId().toString();
        const dcLayId = new ObjectId().toString();
        items.push({
          _id: scbId,
          equipmentName: `${invName}-SCB-${j + 1}`,
          parentId: invId,
          assetId: combR.projectAssetId,
          projectId,
          assetType: 'Combiner Box',
          hierarchyLevel: 'Combiner Box',
          connections: [],
          zone: 'Z01'
        });
        items.push({
          _id: dcLayId,
          equipmentName: `${invName}-SCB-${j + 1} · DC`,
          parentId: scbId,
          assetId: pvR.projectAssetId,
          projectId,
          assetType: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
          hierarchyLevel: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
          connections: [],
          zone: 'Z01',
          solarDcLayout: {
            stringsPerInverter: Math.max(1, nStr),
            modulesPerString: counts.modulesPerString,
            combinerBoxCount: 0
          }
        });
      }
    }

    if (counts.includeDcCable && dcR) {
      const dcId = new ObjectId().toString();
      items.push({
        _id: dcId,
        equipmentName: `${invName}-DCC`,
        parentId: invId,
        assetId: dcR.projectAssetId,
        projectId,
        assetType: mapToBackendAssetType(displayTypeForRole('dcCable', catalog)),
        hierarchyLevel: mapToBackendAssetType(displayTypeForRole('dcCable', catalog)),
        connections: [{ connectedTo: invId, type: 'DC' }],
        zone: 'Z01'
      });
    }

    const addAcChain =
      Boolean(counts.includeAcSide && acR && ltR && trR) &&
      shouldAttachAcChain(i, counts.inverters, counts.transformerCount);

    if (addAcChain) {
      const acId = new ObjectId().toString();
      items.push({
        _id: acId,
        equipmentName: `${invName}-ACC`,
        parentId: invId,
        assetId: acR.projectAssetId,
        projectId,
        assetType: mapToBackendAssetType(displayTypeForRole('acCable', catalog)),
        hierarchyLevel: mapToBackendAssetType(displayTypeForRole('acCable', catalog)),
        connections: [{ connectedTo: invId, type: 'AC' }],
        zone: 'Z01'
      });

      const ltId = new ObjectId().toString();
      items.push({
        _id: ltId,
        equipmentName: `${invName}-LTP`,
        parentId: acId,
        assetId: ltR.projectAssetId,
        projectId,
        assetType: mapToBackendAssetType(displayTypeForRole('ltPanel', catalog)),
        hierarchyLevel: mapToBackendAssetType(displayTypeForRole('ltPanel', catalog)),
        connections: [{ connectedTo: acId, type: 'AC' }],
        zone: 'Z01'
      });

      const trId = new ObjectId().toString();
      items.push({
        _id: trId,
        equipmentName: `${invName}-TRF`,
        parentId: ltId,
        assetId: trR.projectAssetId,
        projectId,
        assetType: mapToBackendAssetType(displayTypeForRole('transformer', catalog)),
        hierarchyLevel: mapToBackendAssetType(displayTypeForRole('transformer', catalog)),
        connections: [{ connectedTo: ltId, type: 'AC' }],
        zone: 'Z01'
      });
    }
  }

  const summaryLines = [
    `Inverters: ${counts.inverters}`,
    useCombiner
      ? `Strings per inverter (on Solar DC Layout under each SCB): ${counts.stringsPerInverter}`
      : `Strings per inverter (on Solar DC Layout under inverter): ${counts.stringsPerInverter}`,
    `Modules per string (M1…M${counts.modulesPerString}): ${counts.modulesPerString}`,
    `Total modules (derived): ${totalStrings * counts.modulesPerString}`,
    useCombiner
      ? `String combiner boxes (equipment + DC layout child each): ${counts.combinerCount}`
      : 'String combiner boxes: none',
    `Transformers (planned): ${counts.transformerCount}`
  ];
  return { items, summary: summaryLines.join('\n') };
}

/** Virtual tree nodes for UI (wizard preview + tree view) — IDs use `::` and are not Mongo ObjectIds */
export function expandSolarLayoutToVirtualNodes(
  inverterEquipmentId: string,
  inverterName: string,
  layout: SolarDcLayout
): VisualEquipment[] {
  if (countSolarVirtualNodesIfExpanded(layout) > SOLAR_VIRTUAL_EXPAND_MAX_NODES) {
    return [buildDcStructureSummaryNode(inverterEquipmentId, inverterName, layout)];
  }

  const nodes: VisualEquipment[] = [];
  for (let s = 1; s <= layout.stringsPerInverter; s++) {
    const sid = `${inverterEquipmentId}::str-${s}`;
    const modIds: string[] = [];
    const nMods = modulesForStringIndex(layout, s);
    for (let m = 1; m <= nMods; m++) {
      const mid = `${inverterEquipmentId}::str-${s}::mod-${m}`;
      modIds.push(mid);
      nodes.push({
        id: mid,
        parentEquipmentId: sid,
        childEquipmentIds: [],
        name: `M${m}`,
        assetType: 'Module',
        assetCategory: 'generation',
        isVirtual: true,
        projectAssetName: 'Module'
      });
    }
    nodes.push({
      id: sid,
      parentEquipmentId: inverterEquipmentId,
      childEquipmentIds: modIds,
      name: `${inverterName}-STR-${s}`,
      assetType: 'String',
      assetCategory: 'string',
      isVirtual: true,
      projectAssetName: 'String'
    });
  }
  return nodes;
}

/**
 * Virtual string rows only (no module children). Modules attach when the user expands each string.
 */
export function expandSolarLayoutToVirtualStringNodesOnly(
  parentEquipmentId: string,
  parentName: string,
  layout: SolarDcLayout
): VisualEquipment[] {
  if (countSolarVirtualNodesIfExpanded(layout) > SOLAR_VIRTUAL_EXPAND_MAX_NODES) {
    return [buildDcStructureSummaryNode(parentEquipmentId, parentName, layout)];
  }
  if (layout.stringsPerInverter < 1) return [];

  const nodes: VisualEquipment[] = [];
  for (let s = 1; s <= layout.stringsPerInverter; s++) {
    const sid = `${parentEquipmentId}::str-${s}`;
    const nMods = modulesForStringIndex(layout, s);
    nodes.push({
      id: sid,
      parentEquipmentId: parentEquipmentId,
      childEquipmentIds: [],
      name: `${parentName}-STR-${s}`,
      assetType: 'String',
      assetCategory: 'string',
      isVirtual: true,
      virtualModulesLazy: true,
      pvModuleCount: nMods,
      projectAssetName: 'String'
    });
  }
  return nodes;
}

/**
 * Module count on a String row: prefer persisted `pvModuleCount`; else legacy `pvModuleSlots` length.
 */
export function getStringEquipmentModuleCount(eq: Record<string, unknown>): number {
  const n = eq.pvModuleCount;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 0) {
    return Math.min(Math.floor(n), 1_000_000);
  }
  const slots = eq.pvModuleSlots;
  if (Array.isArray(slots)) return slots.length;
  return 0;
}

/**
 * Virtual module boxes under a String from `pvModuleCount` (no persisted slot array).
 * Renders M1…Mn as separate nodes; above cap → one summary row.
 */
export function expandStringModuleCountToVirtualNodes(
  stringEquipmentId: string,
  moduleCount: number
): VisualEquipment[] {
  const n = Math.max(0, Math.floor(moduleCount));
  if (n <= 0) return [];
  const cap = TREE_PERF.STRING_PV_MODULE_BOXES_MAX;
  if (n > cap) {
    return [
      {
        id: `${stringEquipmentId}::pv-modules-summary`,
        parentEquipmentId: stringEquipmentId,
        childEquipmentIds: [],
        name: `PV modules · ${n} (open string in equipment details)`,
        assetType: 'Module',
        assetCategory: 'generation',
        isVirtual: true,
        isPvSlotsSummary: true,
        projectAssetName: 'Module summary'
      }
    ];
  }
  return Array.from({ length: n }, (_, idx) => ({
    id: `${stringEquipmentId}::mod-${idx}`,
    parentEquipmentId: stringEquipmentId,
    childEquipmentIds: [],
    name: `M${idx + 1}`,
    assetType: 'Module',
    assetCategory: 'generation',
    isVirtual: true,
    projectAssetName: 'Module'
  }));
}

/**
 * Like {@link expandSolarLayoutToVirtualStringNodesOnly}, plus module virtuals for string ids in `expandedVirtualStringIds`.
 */
export function expandSolarLayoutWithLazyModuleVirtualization(
  parentEquipmentId: string,
  parentName: string,
  layout: SolarDcLayout,
  expandedVirtualStringIds: ReadonlySet<string>
): VisualEquipment[] {
  const strings = expandSolarLayoutToVirtualStringNodesOnly(parentEquipmentId, parentName, layout);
  if (strings.length === 1 && strings[0].isDcStructureSummary) return strings;

  const modules: VisualEquipment[] = [];
  const out: VisualEquipment[] = [];
  for (const strNode of strings) {
    if (strNode.isDcStructureSummary) {
      out.push(strNode);
      continue;
    }
    const nMods = strNode.pvModuleCount ?? 0;
    const shouldExpandMods =
      strNode.virtualModulesLazy === true && expandedVirtualStringIds.has(strNode.id) && nMods > 0;
    if (!shouldExpandMods) {
      out.push(strNode);
      continue;
    }
    const modNodes = expandStringModuleCountToVirtualNodes(strNode.id, nMods);
    modules.push(...modNodes);
    out.push({
      ...strNode,
      virtualModulesLazy: false,
      childEquipmentIds: modNodes.map((m) => m.id)
    });
  }
  return [...out, ...modules];
}

function shouldAttachAcChain(
  inverterIndex1: number,
  totalInverters: number,
  transformerCount: number
): boolean {
  if (transformerCount >= totalInverters) return true;
  if (transformerCount === 1 && totalInverters > 1) return inverterIndex1 === 1;
  return inverterIndex1 <= transformerCount;
}

function distributeStringsAcrossCombiners(stringCount: number, combinerBoxCount: number): number[] {
  if (combinerBoxCount <= 0 || stringCount <= 0) return [];
  const base = Math.floor(stringCount / combinerBoxCount);
  const rem = stringCount % combinerBoxCount;
  return Array.from({ length: combinerBoxCount }, (_, j) => (j < rem ? base + 1 : base));
}
